import { Tooltip } from 'react-tooltip';
import { MdDownload } from 'react-icons/md';
import { MdOutlineReceiptLong } from 'react-icons/md';
import { FaDownload } from 'react-icons/fa6';
import { FundRequestExpenseTypes } from '~/enums/fundRequest';
import { useState } from 'react';
import JSZip from 'jszip';
import { apiUrl, authHeaders } from '~/services/auth';

interface FileItem {
    fileName: string;
    imageUrl: string;
    formKey: string;
    expenseCategory: FundRequestExpenseTypes;
    uploadIndex: number;
}

interface FilesTabProps {
    files: FileItem[];
    onReceiptClick?: (formKey: string, expenseCategory: FundRequestExpenseTypes, uploadIndex: number) => void;
    fundRequestId?: string | number;
}

const FilesTab = ({ files, onReceiptClick, fundRequestId }: FilesTabProps) => {
    const [isDownloadingAll, setIsDownloadingAll] = useState(false);

    const downloadAllFiles = async () => {
        if (files.length === 0) {
            return;
        }

        setIsDownloadingAll(true);
        
        try {
            const zip = new JSZip();
            const promises: Promise<void>[] = [];

            // Add each file to the zip
            files.forEach((file, index) => {
                // Check if URL is from S3
                const isS3Url = file.imageUrl.includes('s3.amazonaws.com') || file.imageUrl.includes('.s3.');
                
                // For S3 URLs: use plain fetch with no headers to avoid CORS preflight
                // For non-S3 URLs: use auth headers
                const promise = fetch(file.imageUrl, isS3Url ? {} : {
                    headers: authHeaders(),
                    credentials: 'include',
                })
                    .then(response => {
                        if (!response.ok) {
                            throw new Error(`HTTP ${response.status}: ${response.statusText}`);
                        }
                        return response.blob();
                    })
                    .then(blob => {
                        // Create a unique filename to avoid duplicates
                        const extension = file.fileName?.split('.').pop() || '';
                        const nameWithoutExt = file.fileName?.replace(/\.[^/.]+$/, '') || `file_${index + 1}`;
                        const uniqueFilename = `${nameWithoutExt}_${index + 1}.${extension}`;
                        
                        zip.file(uniqueFilename, blob);
                    })
                    .catch(error => {
                        // Add a text file noting the error instead
                        const errorFileName = `ERROR_${file.fileName || `file_${index + 1}`}.txt`;
                        zip.file(errorFileName, 
                                 `Failed to download this file: ${error.message}\nOriginal URL: ${file.imageUrl}`);
                    });

                promises.push(promise);
            });

            // Wait for all downloads to complete
            await Promise.all(promises);

            // Generate the zip file
            const zipBlob = await zip.generateAsync({ type: 'blob' });

            // Create download link
            const link = document.createElement('a');
            const url = URL.createObjectURL(zipBlob);
            link.href = url;
            
            // Create filename with fund request ID and current date
            const dateStr = new Date().toISOString().split('T')[0];
            const filename = fundRequestId 
                ? `fundrequest_${fundRequestId}_files_${dateStr}.zip`
                : `fund_request_files_${dateStr}.zip`;
            link.download = filename;
            document.body.appendChild(link);
            link.click();
            
            // Cleanup
            document.body.removeChild(link);
            URL.revokeObjectURL(url);

        } catch (error) {
            console.error('Error creating zip file:', error);
        } finally {
            setIsDownloadingAll(false);
        }
    };

    return (
        <>
            <div className='mb-4 flex items-center justify-between'>
                <h2 className='text-[22px]/[24px] font-semibold'>
                    Files <span>({files?.length})</span>
                </h2>
                {files.length > 0 && (
                    <button
                        onClick={downloadAllFiles}
                        disabled={isDownloadingAll}
                        className={`flex items-center gap-[8px] rounded-full px-[24px] py-[7px] text-[14px]/[18px] font-bold uppercase transition-colors duration-200 ${
                            isDownloadingAll 
                                ? 'text-gray-400 cursor-not-allowed' 
                                : 'text-[#00AEB3] hover:bg-[#e6f7f8]'
                        }`}
                        title="Download all files as ZIP"
                    >
                        {isDownloadingAll ? (
                            <>
                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current"></div>
                                <span>Downloading...</span>
                            </>
                        ) : (
                            <>
                                <FaDownload className='text-[16px]' />
                                <span>Download All</span>
                            </>
                        )}
                    </button>
                )}
            </div>
            <div className='m-0 w-full space-y-0 p-0'>
                {files?.map((item, index) => {
                    const isImage = /\.(jpe?g|png|gif|bmp|webp)$/i.test(item.fileName);
                    const isPdf = /\.pdf$/i.test(item.fileName);
                    const isPreviewable = isImage || isPdf;
                    const tooltipId = `my-tooltip-${index}`;

                    const handleReceiptClick = () => {
                        if (onReceiptClick) {
                            onReceiptClick(item.formKey, item.expenseCategory, item.uploadIndex);
                        }
                    };

                    return (
                        <div key={index} className='group'>
                            <div className='text-blue-500 flex items-center gap-2'>
                                {isPreviewable ? (
                                    <button
                                        onClick={handleReceiptClick}
                                        className='cursor-pointer'
                                        title='View document'
                                    >
                                        <MdOutlineReceiptLong className='text-[#00aeb3]' size={20} />
                                    </button>
                                ) : (
                                    <a
                                        href={item.imageUrl}
                                        download
                                        className='bg-blue-500 rounded-lg px-0 py-1 pr-2 text-white'
                                    >
                                        <MdDownload className='text-[#00aeb3]' />
                                    </a>
                                )}

                                <span
                                    className='inline-block w-[88%] truncate'
                                    data-tooltip-id={tooltipId}
                                >
                                    {item.fileName}
                                </span>
                            </div>

                            <Tooltip
                                id={tooltipId}
                                place='top'
                                content={item.fileName}
                            />
                        </div>
                    );
                })}
            </div>
        </>
    );
};

export default FilesTab;
